// Governance Service - handles all governance-related API calls
import {
  Comment,
  Proposal,
  User,
} from '@/components/entities/community_sub-elects/governance/types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const API_TIMEOUT = 10000; // 10 seconds

// API Response Types
interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  error?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface CommentResponse {
  success: boolean;
  comment?: Comment;
  error?: string;
}

interface CommentsResponse {
  success: boolean;
  comments?: Comment[];
  error?: string;
}

// HTTP Client wrapper for API calls
class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: AbortSignal.timeout(this.timeout),
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<T>(`${endpoint}${queryString}`, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Initialize API client for future backend integration
const apiClient = new ApiClient(API_BASE_URL);

class GovernanceService {
  private baseUrl = '/api/governance';

  // ===== PROPOSAL MANAGEMENT (Future Backend Integration) =====

  async getProposals(params?: {
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
    communityId?: string;
  }): Promise<PaginatedResponse<Proposal>> {
    try {
      // When backend is ready, replace with:
      // const response = await apiClient.get<PaginatedResponse<Proposal>>('/proposals', params);
      // return response.data;

      // Mock implementation for now
      await new Promise((resolve) => setTimeout(resolve, 500));
      return {
        data: [],
        pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
      };
    } catch (error) {
      throw new Error('Failed to fetch proposals', { cause: error });
    }
  }

  async getProposalById(id: string): Promise<Proposal | null> {
    try {
      // When backend is ready, replace with:
      // const response = await apiClient.get<Proposal>(`/proposals/${id}`);
      // return response.data;

      // Mock implementation for now
      await new Promise((resolve) => setTimeout(resolve, 300));
      return null;
    } catch (error) {
      throw new Error('Failed to fetch proposal', { cause: error });
    }
  }

  async createProposal(proposalData: Partial<Proposal>): Promise<Proposal> {
    try {
      // When backend is ready, replace with:
      // const response = await apiClient.post<Proposal>('/proposals', proposalData);
      // return response.data;

      // Mock implementation for now
      await new Promise((resolve) => setTimeout(resolve, 800));
      throw new Error('Backend integration pending');
    } catch (error) {
      throw new Error('Failed to create proposal', { cause: error });
    }
  }

  async updateProposal(id: string, updates: Partial<Proposal>): Promise<Proposal> {
    try {
      // When backend is ready, replace with:
      // const response = await apiClient.put<Proposal>(`/proposals/${id}`, updates);
      // return response.data;

      // Mock implementation for now
      await new Promise((resolve) => setTimeout(resolve, 600));
      throw new Error('Backend integration pending');
    } catch (error) {
      throw new Error('Failed to update proposal', { cause: error });
    }
  }

  // ===== FILE MANAGEMENT (Future Backend Integration) =====

  async uploadFile(
    file: File,
    proposalId: string,
  ): Promise<{
    id: string;
    name: string;
    type: string;
    size: number;
    url: string;
    uploadedBy: string;
    uploadedAt: string;
  }> {
    try {
      // When backend is ready, use multipart upload:
      /*
      const formData = new FormData();
      formData.append('file', file);
      formData.append('proposalId', proposalId);

      const response = await fetch(`${API_BASE_URL}/files/upload`, {
        method: 'POST',
        body: formData,
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) throw new Error('File upload failed');
      const data = await response.json();
      return data.data;
      */

      // Mock implementation for now
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return {
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: file.type,
        size: file.size,
        url: `/files/${Math.random().toString(36).substr(2, 9)}`,
        uploadedBy: 'current-user',
        uploadedAt: new Date().toISOString(),
      };
    } catch (error) {
      throw new Error('File upload failed', { cause: error });
    }
  }

  // ===== EXISTING FUNCTIONALITY (Mock Implementation) =====

  // Get comments for a proposal
  async getComments(proposalId: string): Promise<CommentsResponse> {
    try {
      // For now, return mock data - replace with real API call
      const mockComments: Comment[] = [
        {
          id: '1',
          proposalId,
          authorId: 'user1',
          content:
            'I think this is a great idea! The dog park would be a wonderful addition to our community.',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
          replies: [],
        },
        {
          id: '2',
          proposalId,
          authorId: 'user2',
          content:
            'What about the maintenance costs? We should consider the long-term budget impact.',
          timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
          replies: [
            {
              id: '3',
              proposalId,
              authorId: 'user3',
              content:
                'Good point! Maybe we could set up a community volunteer schedule for basic maintenance.',
              timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(), // 20 hours ago
              parentId: '2',
              replies: [],
            },
          ],
        },
      ];

      return {
        success: true,
        comments: mockComments,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch comments',
      };
    }
  }

  // Add a new comment
  async addComment(
    proposalId: string,
    content: string,
    parentId?: string,
  ): Promise<CommentResponse> {
    try {
      // For now, return mock success - replace with real API call
      const newComment: Comment = {
        id: Math.random().toString(36).substr(2, 9),
        proposalId,
        authorId: 'current-user',
        content,
        timestamp: new Date().toISOString(),
        parentId,
        replies: [],
      };

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      return {
        success: true,
        comment: newComment,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to post comment',
      };
    }
  }

  // Update a comment
  async updateComment(commentId: string, content: string): Promise<CommentResponse> {
    try {
      // Mock implementation - replace with real API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to update comment',
      };
    }
  }

  // Delete a comment
  async deleteComment(commentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Mock implementation - replace with real API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to delete comment',
      };
    }
  }

  // Vote on a proposal with cryptographic verification
  async castVote(
    proposalId: string,
    option: 'yes' | 'no' | 'abstain',
    comment?: string,
  ): Promise<{
    success: boolean;
    error?: string;
    verificationCode?: string;
    voteId?: string;
  }> {
    try {
      const voteId = this.generateVoteId();
      const verificationCode = this.generateVerificationCode(voteId);

      // In future backend implementation, this will:
      // 1. Store the vote in the database
      // 2. Update the election audit trail

      // Mock database storage delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      return {
        success: true,
        verificationCode,
        voteId,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to cast vote',
      };
    }
  }

  // Generate unique vote ID
  private generateVoteId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `vote_${timestamp}_${random}`;
  }

  /**
   * Generate a short reference number for the voter's own records.
   *
   * This is a support/lookup reference ONLY. It is not a cryptographic
   * receipt and proves nothing about how a vote was recorded or counted.
   */
  private generateVerificationCode(voteId: string): string {
    let hash = 0;
    const str = voteId + new Date().toISOString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Convert to base36 and take first 8 characters, uppercase
    const code = Math.abs(hash).toString(36).toUpperCase().substring(0, 8);
    return code.padEnd(8, '0'); // Ensure 8 characters
  }

  // Sign a proposal
  async signProposal(
    proposalId: string,
    toolboxId?: string,
  ): Promise<{ success: boolean; error?: string; signatureCount?: number }> {
    try {
      // Mock implementation - replace with real API call
      await new Promise((resolve) => setTimeout(resolve, 600));

      console.log(`Proposal signed: ${proposalId} by Toolbox ID: ${toolboxId || 'anonymous'}`);

      // Mock return with updated signature count
      const currentSignatures = Math.floor(Math.random() * 50) + 70; // Random between 70-120

      return {
        success: true,
        signatureCount: currentSignatures + 1,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to sign proposal with Toolbox ID',
      };
    }
  }

  // Remove signature from a proposal
  async removeSignature(
    proposalId: string,
  ): Promise<{ success: boolean; error?: string; signatureCount?: number }> {
    try {
      // Mock implementation - replace with real API call
      await new Promise((resolve) => setTimeout(resolve, 400));

      console.log(`Signature removed from proposal: ${proposalId}`);

      // Mock return with updated signature count
      const currentSignatures = Math.floor(Math.random() * 50) + 70; // Random between 70-120

      return {
        success: true,
        signatureCount: Math.max(0, currentSignatures - 1),
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to remove signature',
      };
    }
  }

  // Format relative time for comments
  formatRelativeTime(timestamp: string): string {
    const now = new Date();
    const commentTime = new Date(timestamp);
    const diffInMs = now.getTime() - commentTime.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInDays < 1) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return commentTime.toLocaleDateString();
    }
  }

  // Get user display name (mock for now)
  getUserDisplayName(authorId: string): string {
    // In a real app, this would fetch user data
    const mockUsers: Record<string, string> = {
      user1: 'Sarah Johnson',
      user2: 'Mike Chen',
      user3: 'Lisa Garcia',
      'current-user': 'You',
    };

    return mockUsers[authorId] || 'Community Member';
  }
}

// Utility functions for data transformation and calculations
export const governanceUtils = {
  // Calculate days remaining for voting
  calculateDaysRemaining: (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  },

  // Format vote percentage
  formatVotePercentage: (count: number, total: number): string => {
    if (total === 0) return '0%';
    return `${Math.round((count / total) * 100)}%`;
  },

  // Check if proposal has required signatures
  hasRequiredSignatures: (proposal: Proposal, requiredCount: number = 5): boolean => {
    return proposal.signatures.length >= requiredCount;
  },

  // Check if voting period is active
  isVotingActive: (proposal: Proposal): boolean => {
    const now = new Date();
    const start = new Date(proposal.votingConfig.startDate);
    const end = new Date(proposal.votingConfig.endDate);
    return now >= start && now <= end;
  },

  // Generate proposal summary
  generateProposalSummary: (proposal: Proposal): string => {
    const daysRemaining = governanceUtils.calculateDaysRemaining(proposal.votingConfig.endDate);
    const signatureCount = proposal.signatures.length;
    const voteCount = proposal.votes.length;

    return `${proposal.title} - ${signatureCount} signatures, ${voteCount} votes, ${daysRemaining} days remaining`;
  },

  // Format currency for budget displays
  formatCurrency: (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  },

  // Calculate budget total from breakdown
  calculateBudgetTotal: (breakdown: Array<{ cost: number }>): number => {
    return breakdown.reduce((total, item) => total + item.cost, 0);
  },

  // Validate proposal data before submission
  validateProposal: (proposal: Partial<Proposal>): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!proposal.title?.trim()) {
      errors.push('Title is required');
    }
    if (!proposal.description?.trim()) {
      errors.push('Description is required');
    }
    if (!proposal.justification?.trim()) {
      errors.push('Justification is required');
    }
    if (proposal.budget && proposal.budget.totalCost <= 0) {
      errors.push('Budget must be greater than 0');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  },
};

export const governanceService = new GovernanceService();
export default governanceService;
